/**
 * Scribe – stockage local de l'enregistrement (IndexedDB du navigateur) : chaque partie de 10 s y est écrite AVANT
 * d'être déposée dans S3, puis effacée une fois déposée. Un rechargement de la page, un plantage ou une coupure du réseau
 * ne perd donc que la partie en cours ; au retour, la réunion inachevée est retrouvée (« Reprendre » ou « Terminer »).
 * Deux magasins : `sessions` (réunion en cours dans ce navigateur : titre, format, dernière séquence, durée enregistrée)
 * et `parties` (audio en attente de dépôt, clé [réunion, séquence, numéro], donc lues dans l'ordre).
 * Sans IndexedDB (navigation privée de certains navigateurs, accès refusé) : stockage en mémoire de l'onglet – on
 * enregistre quand même, mais une réunion n'est pas retrouvée après un rechargement.
 * Rien n'est gardé au-delà du dépôt : l'audio ne sert qu'au traitement (docs/SCRIBE.md › Nettoyage du stockage).
 */
import type { TypePartieScribe } from '@agora/shared';

/** Réunion enregistrée dans ce navigateur, tant qu'elle n'est pas terminée (fin envoyée au serveur). */
export interface SessionScribe {
  reunion: string;
  /** Utilisateur AGORA (id) : une session n'est proposée qu'à son auteur. */
  utilisateur: string;
  titre: string;
  type: TypePartieScribe;
  /** Dernière séquence ouverte (une nouvelle toutes les 10 min et à chaque reprise). */
  sequence: number;
  /** Durée enregistrée (somme des parties écrites), en millisecondes. */
  dureeMs: number;
  debut: string;
  /** Arrêt demandé : reste à déposer les parties en attente, puis à envoyer la fin. */
  terminee: boolean;
  /** Son des participants à distance (partage de Teams) enregistré aussi : à partager de nouveau après un rechargement. */
  distance?: boolean;
}

/** Partie de 10 s (ou moins : fin de séquence) en attente de dépôt. */
export interface PartieScribe {
  reunion: string;
  sequence: number;
  numero: number;
  type: TypePartieScribe;
  dureeMs: number;
  audio: Blob;
}

export interface StockageScribe {
  /** Persistant (IndexedDB) : une réunion survit au rechargement de la page. */
  readonly persistant: boolean;
  sessions(utilisateur: string): Promise<SessionScribe[]>;
  ecrireSession(s: SessionScribe): Promise<void>;
  /** Efface la session et ses parties en attente. */
  oublier(reunion: string): Promise<void>;
  ajouterPartie(p: PartieScribe): Promise<void>;
  /** Parties en attente de la réunion, dans l'ordre (séquence, numéro). */
  parties(reunion: string): Promise<PartieScribe[]>;
  retirerPartie(p: Pick<PartieScribe, 'reunion' | 'sequence' | 'numero'>): Promise<void>;
}

/** Stockage en mémoire (repli sans IndexedDB, tests). */
export function stockageMemoire(): StockageScribe {
  const sessions = new Map<string, SessionScribe>();
  const parties = new Map<string, PartieScribe>();
  const cle = (p: Pick<PartieScribe, 'reunion' | 'sequence' | 'numero'>) => `${p.reunion}|${p.sequence}|${p.numero}`;
  return {
    persistant: false,
    sessions: async (u) => [...sessions.values()].filter((s) => s.utilisateur === u).map((s) => ({ ...s })),
    ecrireSession: async (s) => { sessions.set(s.reunion, { ...s }); },
    oublier: async (reunion) => {
      sessions.delete(reunion);
      for (const [k, p] of parties) if (p.reunion === reunion) parties.delete(k);
    },
    ajouterPartie: async (p) => { parties.set(cle(p), p); },
    parties: async (reunion) => [...parties.values()]
      .filter((p) => p.reunion === reunion)
      .sort((a, b) => a.sequence - b.sequence || a.numero - b.numero),
    retirerPartie: async (p) => { parties.delete(cle(p)); },
  };
}

const BASE = 'agora-scribe';
const SESSIONS = 'sessions';
const PARTIES = 'parties';

const enPromesse = <T>(r: IDBRequest<T>) => new Promise<T>((ok, ko) => {
  r.onsuccess = () => ok(r.result);
  r.onerror = () => ko(r.error);
});

function ouvrirBase(idb: IDBFactory): Promise<IDBDatabase> {
  return new Promise((ok, ko) => {
    const r = idb.open(BASE, 1);
    r.onupgradeneeded = () => {
      const db = r.result;
      if (!db.objectStoreNames.contains(SESSIONS)) db.createObjectStore(SESSIONS, { keyPath: 'reunion' });
      if (!db.objectStoreNames.contains(PARTIES)) db.createObjectStore(PARTIES, { keyPath: ['reunion', 'sequence', 'numero'] });
    };
    r.onsuccess = () => ok(r.result);
    r.onerror = () => ko(r.error);
    r.onblocked = () => ko(new Error('IndexedDB bloquée'));
  });
}

/** Toutes les parties d'une réunion : clés [réunion, 0, 0] à [réunion, ∞, ∞]. */
const plage = (reunion: string) => IDBKeyRange.bound([reunion, 0, 0], [reunion, Infinity, Infinity]);

function stockageIndexedDb(db: IDBDatabase): StockageScribe {
  const transaction = async <T>(magasins: string[], mode: IDBTransactionMode, faire: (t: IDBTransaction) => Promise<T> | T): Promise<T> => {
    const t = db.transaction(magasins, mode);
    const fini = new Promise<void>((ok, ko) => {
      t.oncomplete = () => ok();
      t.onerror = () => ko(t.error);
      t.onabort = () => ko(t.error ?? new Error('transaction annulée'));
    });
    const resultat = await faire(t);
    await fini;
    return resultat;
  };
  return {
    persistant: true,
    sessions: (u) => transaction([SESSIONS], 'readonly', (t) => enPromesse(t.objectStore(SESSIONS).getAll() as IDBRequest<SessionScribe[]>))
      .then((l) => l.filter((s) => s.utilisateur === u)),
    ecrireSession: (s) => transaction([SESSIONS], 'readwrite', (t) => { t.objectStore(SESSIONS).put(s); }),
    oublier: (reunion) => transaction([SESSIONS, PARTIES], 'readwrite', (t) => {
      t.objectStore(SESSIONS).delete(reunion);
      t.objectStore(PARTIES).delete(plage(reunion));
    }),
    ajouterPartie: (p) => transaction([PARTIES], 'readwrite', (t) => { t.objectStore(PARTIES).put(p); }),
    parties: (reunion) => transaction([PARTIES], 'readonly', (t) => enPromesse(t.objectStore(PARTIES).getAll(plage(reunion)) as IDBRequest<PartieScribe[]>)),
    retirerPartie: (p) => transaction([PARTIES], 'readwrite', (t) => { t.objectStore(PARTIES).delete([p.reunion, p.sequence, p.numero]); }),
  };
}

/** Stockage du navigateur (IndexedDB), sinon en mémoire. */
export async function ouvrirStockage(): Promise<StockageScribe> {
  try {
    if (typeof indexedDB === 'undefined') return stockageMemoire();
    return stockageIndexedDb(await ouvrirBase(indexedDB));
  } catch {
    return stockageMemoire();
  }
}
